import { newRequestId } from '@hq/utils';
import { matchCacheRule, type CacheRule } from './cache';
import { ApiCode, ApiError, httpStatusToCode, type ApiEnvelope } from './types/result';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * 请求体的轻量摘要（djb2），仅用于识别"同一次提交的重试"，不做任何安全用途。
 * FormData 无法稳定序列化，直接返回占位符（这类请求不会两类并存，碰撞无实际影响）。
 */
function digest(body: string | FormData | null | undefined): string {
  if (body === null || body === undefined) return '0';
  if (typeof body !== 'string') return 'form';

  let hash = 5381;
  for (let i = 0; i < body.length; i += 1) hash = ((hash << 5) + hash + body.charCodeAt(i)) | 0;
  return `${body.length}:${hash >>> 0}`;
}

/** 交由适配器执行的最小请求描述（Web: fetch/axios；RN: fetch；小程序: Taro.request）。 */
export interface HttpRequest {
  url: string;
  method: HttpMethod;
  headers: Record<string, string>;
  body?: string | FormData | null;
  signal?: AbortSignal;
}

export interface HttpResponse {
  status: number;
  /** header 名一律小写。 */
  headers: Record<string, string>;
  /** 适配器负责 JSON 解析；空响应为 null。 */
  body: unknown;
}

export interface HttpAdapter {
  send(request: HttpRequest): Promise<HttpResponse>;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  path: string;
  method?: HttpMethod;
  query?: Record<string, QueryValue>;
  /** JSON 请求体。 */
  body?: unknown;
  /** 上传用表单，与 body 互斥。 */
  form?: FormData;
  headers?: Record<string, string>;
  /** 写请求默认注入 X-Request-Id；重试请复用同一个 requestId。 */
  idempotent?: boolean;
  requestId?: string;
  signal?: AbortSignal;
}

/** GET 内存缓存配置：仅缓存只读请求，写请求成功后自动清空。 */
export interface HttpCacheOptions {
  /** 规则未命中时的默认 TTL；0/缺省表示不缓存（仍做并发去重）。 */
  ttlMs?: number;
  /** 按路径前缀覆盖 TTL。 */
  rules?: readonly CacheRule[];
  /** 最多保留条目数，默认 120，防止长时间运行内存膨胀。 */
  maxEntries?: number;
  /** 缓存作用域（租户/用户标识），避免切换账号后命中旧数据。 */
  scope?: () => string | null | undefined;
}

export interface HttpClientOptions {
  adapter: HttpAdapter;
  /** 如 `https://hq.hospital.xxx.cn/api`；不传则用相对路径（依赖同源反代）。 */
  baseUrl?: string;
  /** 令牌来源：Web 内存/sessionStorage、RN SecureStore、小程序 Storage。 */
  resolveToken?: () => string | null | undefined;
  /** 401 统一处理（清理登录态、跳登录页）。 */
  onUnauthorized?: (error: ApiError) => void;
  /** 幂等键生成器，默认 newRequestId。 */
  resolveRequestId?: () => string;
  /** 只读 GET 缓存（小程序/App 建议开启；Web 由 TanStack Query 负责）。 */
  cache?: HttpCacheOptions;
}

export interface HttpClient {
  readonly baseUrl: string;
  /** 原始响应（上传、下载等非 ApiResult 场景）。 */
  send(options: RequestOptions): Promise<HttpResponse>;
  /** 解包 `{ code, message, data }`，失败抛 ApiError。 */
  request<T>(options: RequestOptions): Promise<T>;
  /** 清空 GET 缓存（登出、切机构或需要强制刷新时调用）。 */
  clearCache(): void;
}

export function toQuery(page: object): Record<string, QueryValue> {
  const query: Record<string, QueryValue> = {};
  for (const [key, value] of Object.entries(page)) {
    if (value === undefined || value === null || value === '') continue;
    // NaN / Infinity 不是合法查询参数（如 `?beginId=NaN`），会让服务端绑定失败，直接丢弃
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    query[key] = value as QueryValue;
  }
  return query;
}

function buildUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const root = baseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${root}${suffix}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}

function isEnvelope(body: unknown): body is ApiEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'code' in body && 'message' in body;
}

function readMessage(body: unknown, status: number): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return `请求失败（HTTP ${status}）`;
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const { adapter, baseUrl = '', resolveToken, onUnauthorized } = options;
  const nextRequestId = options.resolveRequestId ?? newRequestId;

  const cacheRules = options.cache?.rules ?? [];
  const cacheFallbackTtl = options.cache?.ttlMs ?? 0;
  const cacheMaxEntries = options.cache?.maxEntries ?? 120;
  const cacheScope = options.cache?.scope;
  const responseCache = new Map<string, { response: HttpResponse; expiresAt: number }>();
  /** 同一 GET 并发只发一次（React StrictMode / 多组件同时挂载 / 大屏多面板）。 */
  const inflight = new Map<string, Promise<HttpResponse>>();
  /**
   * 写操作的幂等键暂存：键 = `method path body摘要`。
   * 只在**传输层失败**（超时/断网，没拿到 HTTP 响应）后保留，供同一次操作的重试复用，
   * 避免"服务端其实已受理、客户端换了个新键重发"造成重复建单/重复扣款。
   */
  const pendingWriteKeys = new Map<string, string>();

  const clearCache = (): void => {
    responseCache.clear();
    // 在途请求也要一并作废：换账号/登出时若旧账号的 GET 仍在飞行中，
    // 它完成时会把上一个身份的数据写回新缓存（缓存"复活"成别人的数据）。
    inflight.clear();
    // 换账号后不能复用上一个账号的幂等键（否则新账号的同体请求会被服务端判为重复提交）
    pendingWriteKeys.clear();
  };

  const execute = (
    request: RequestOptions & { body?: string | FormData | null },
    url: string,
    method: HttpMethod
  ): Promise<HttpResponse> => {
    const isRead = method === 'GET' && !request.form && request.body === undefined;

    if (!isRead) {
      return adapter
        .send({ url, method, headers: request.headers ?? {}, body: request.body, signal: request.signal })
        .then((response) => {
          // 写操作成功后清空只读缓存，避免列表/详情读到旧数据
          if (response.status >= 200 && response.status < 300) clearCache();
          return response;
        });
    }

    const ttl = matchCacheRule(request.path, cacheRules, cacheFallbackTtl);
    const key = `${cacheScope?.() ?? ''}|${method} ${url}`;

    if (ttl > 0) {
      const hit = responseCache.get(key);
      if (hit) {
        if (hit.expiresAt > Date.now()) return Promise.resolve(hit.response);
        responseCache.delete(key);
      }
    }

    // 带 AbortSignal 的请求不参与共享：否则首个调用者 abort 会连带中断其他等待者
    const dedupe = request.signal === undefined;
    if (dedupe) {
      const pending = inflight.get(key);
      if (pending) return pending;
    }

    const task = adapter
      .send({ url, method, headers: request.headers ?? {}, signal: request.signal })
      .then((response) => {
        if (ttl > 0 && response.status >= 200 && response.status < 300) {
          if (responseCache.size >= cacheMaxEntries) {
            const oldest = responseCache.keys().next().value;
            if (oldest !== undefined) responseCache.delete(oldest);
          }
          responseCache.set(key, { response, expiresAt: Date.now() + ttl });
        }
        return response;
      })
      .finally(() => {
        if (dedupe) inflight.delete(key);
      });

    if (dedupe) inflight.set(key, task);
    return task;
  };

  const send = async (request: RequestOptions): Promise<HttpResponse> => {
    const method = request.method ?? (request.body !== undefined || request.form ? 'POST' : 'GET');
    const headers: Record<string, string> = { Accept: 'application/json', ...request.headers };

    const token = resolveToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;

    let body: string | FormData | null | undefined;
    if (request.form) {
      body = request.form;
    } else if (request.body !== undefined) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
      body = JSON.stringify(request.body);
    }

    const isWrite = method !== 'GET';
    const idempotent = request.idempotent ?? isWrite;

    // 写操作的幂等键必须"重试可复用"：
    // 超时/断网时客户端拿不到任何 HTTP 响应，**无法判断服务端是否已受理**；
    // 此时换一个新 requestId 重试，后端的 IdempotencyFilter 就认不出是同一次提交
    // → 重复建单/重复扣款。这里把"上次传输失败时用的键"按 method+path+body 暂存，
    // 下次同样的请求直接复用；一旦收到任何 HTTP 响应（服务端已明确答复）就清除。
    const writeKey = isWrite ? `${method} ${request.path} ${digest(body)}` : '';
    const requestId =
      request.requestId ??
      (idempotent
        ? isWrite
          ? (pendingWriteKeys.get(writeKey) ?? nextRequestId())
          : nextRequestId()
        : undefined);

    if (isWrite && !request.requestId && requestId) {
      // 上限保护：异常场景下不应无限增长
      if (pendingWriteKeys.size > 64) pendingWriteKeys.clear();
      pendingWriteKeys.set(writeKey, requestId);
    }

    // 后端 TraceIdMiddleware 会优先复用 X-Request-Id 作为 traceId，响应头回读
    if (requestId) headers['X-Request-Id'] = requestId;

    const url = buildUrl(baseUrl, request.path, request.query);

    try {
      const response = await execute({ ...request, headers, body }, url, method);
      // 拿到响应即视为服务端已给出明确答复，后续重试属于新的一次操作
      if (isWrite && !request.requestId) pendingWriteKeys.delete(writeKey);
      return response;
    } catch (error) {
      // 只有"没拿到 HTTP 响应"的传输层异常才保留幂等键；ApiError 在上面 request() 里抛，不会走到这儿
      throw error;
    }
  };

  const request = async <T>(requestOptions: RequestOptions): Promise<T> => {
    const response = await send(requestOptions);
    const traceId = response.headers['x-trace-id'] ?? (isEnvelope(response.body) ? response.body.traceId : null);
    const { status, body } = response;

    if (status < 200 || status >= 300) {
      const envelope = isEnvelope(body) ? body : null;
      // 资源服务的错误体 code 就是 HTTP 状态码，需先归一为业务码
      const rawCode = envelope?.code;
      const code =
        typeof rawCode === 'number' && rawCode !== status ? rawCode : httpStatusToCode(status);
      const error = new ApiError({
        code,
        message: readMessage(body, status),
        httpStatus: status,
        traceId: traceId ?? null,
        payload: body
      });
      // 401 必须清缓存后再回调：两条失败分支（有/无信封）口径要一致，
      // 只清一条会让"非信封 401"（如网关直出）把上一账号的 GET 数据留给下一个账号。
      if (error.isUnauthorized) {
        clearCache();
        onUnauthorized?.(error);
      }
      throw error;
    }

    if (body === null || body === undefined) return undefined as T;

    if (!isEnvelope(body)) return body as T;

    if (body.code !== ApiCode.Ok) {
      const error = new ApiError({
        code: body.code,
        message: body.message,
        httpStatus: status,
        traceId: body.traceId ?? traceId ?? null,
        payload: body
      });
      if (error.isUnauthorized) {
        clearCache();
        onUnauthorized?.(error);
      }
      throw error;
    }

    return body.data as T;
  };

  return { baseUrl, send, request, clearCache };
}
